import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Search, UserPlus, WifiOff, X } from "lucide-react";
import { PageLoader } from "../context/AuthContext";
import { DoorRow } from "../components/DoorRow";
import { AddGuestSheet, NameSheet } from "../components/DoorSheets";
import { DuplicateNotice } from "../components/DuplicateNotice";
import { api, formatApiError } from "../lib/api";
import { insertSorted, isNetworkError, labelOf, matches, replaceGuest } from "../lib/doorGuests";

const OFFLINE = "You're offline — changes can't be saved";

const useOnline = () => {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
};

const Count = ({ label, value, testid, cls }) => (
  <div className={`flex-1 rounded-xl px-2 py-1.5 text-center ${cls}`}>
    <p data-testid={testid} className="font-display text-xl font-extrabold leading-none">{value}</p>
    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] opacity-80">{label}</p>
  </div>
);

export default function DoorCheckIn() {
  const { id } = useParams();
  const online = useOnline();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState({});
  const [errors, setErrors] = useState({});
  const [netDown, setNetDown] = useState(false);
  const [nameGuest, setNameGuest] = useState(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}/door-guests`).then((r) => setData(r.data)).catch((e) => setLoadError(formatApiError(e)));
  }, [id]);

  const label = labelOf(data?.guest_identifier_label);
  const visible = useMemo(() => (data ? data.guests.filter((g) => matches(g, query)) : []), [data, query]);

  const failed = (err) => {
    if (isNetworkError(err)) setNetDown(true);
    return isNetworkError(err) ? OFFLINE : formatApiError(err);
  };
  const applyResult = (r) => {
    setNetDown(false);
    setData((d) => ({ ...d, guests: replaceGuest(d.guests, r.guest), counts: r.counts }));
  };

  const setCheckIn = useCallback(async (guest, checked) => {
    setPending((p) => ({ ...p, [guest.id]: true }));
    setErrors((e) => ({ ...e, [guest.id]: null }));
    try {
      const { data: r } = await api.put(`/events/${id}/door-guests/${guest.id}/check-in`, { checked_in: checked });
      applyResult(r);
    } catch (err) {
      setErrors((e) => ({ ...e, [guest.id]: { message: failed(err), checked } }));
    } finally {
      setPending((p) => ({ ...p, [guest.id]: false }));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNames = async (guest, form) => {
    try {
      const { data: r } = await api.patch(`/events/${id}/door-guests/${guest.id}`, form);
      applyResult(r);
      setNameGuest(null);
      return "";
    } catch (err) {
      return failed(err);
    }
  };

  const addGuest = async (form) => {
    try {
      const { data: r } = await api.post(`/events/${id}/door-guests`, form);
      setNetDown(false);
      setData((d) => ({ ...d, guests: d.guests.some((g) => g.id === r.guest.id) ? d.guests : insertSorted(d.guests, r.guest), counts: r.counts }));
      setAdding(false);
      if (r.possible_duplicates.length) setNotice({ guest: r.guest, duplicates: r.possible_duplicates });
      return "";
    } catch (err) {
      return failed(err);
    }
  };

  const undoAdd = async () => {
    try {
      const { data: counts } = await api.delete(`/events/${id}/door-guests/${notice.guest.id}`);
      setData((d) => ({ ...d, guests: d.guests.filter((g) => g.id !== notice.guest.id), counts }));
      setNotice(null);
    } catch (err) {
      failed(err);
    }
  };

  if (loadError) return <p data-testid="door-load-error" className="p-6 text-center text-slate-600">{loadError}</p>;
  if (!data) return <PageLoader />;

  return (
    <div data-testid="door-checkin-page" className="font-manrope min-h-[100dvh] bg-white text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] backdrop-blur-xl">
        <div className="mb-2 flex items-center justify-between gap-2">
          <Link to={`/events/${id}`} data-testid="door-back-link" className="inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-slate-500"><ArrowLeft className="h-4 w-4" /> Event</Link>
          <button type="button" data-testid="door-add-btn" onClick={() => setAdding(true)} className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"><UserPlus className="h-4 w-4" /> Add guest</button>
        </div>
        <h1 data-testid="door-heading" className="mb-2 break-words text-sm font-bold text-slate-900">Door check-in <span className="font-semibold text-blue-700">· {label}</span></h1>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input data-testid="door-search-input" type="search" inputMode="search" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${label.toLowerCase()} or name`} className="no-native-clear h-14 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-11 text-base placeholder:text-[15px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-700" />
          {query && <button type="button" aria-label="Clear search" data-testid="door-search-clear" onClick={() => setQuery("")} className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-slate-400"><X className="h-5 w-5" /></button>}
        </div>
        <div className="mt-3 flex gap-2">
          <Count label="Total" value={data.counts.total} testid="door-count-total" cls="bg-slate-100 text-slate-800" />
          <Count label="Checked in" value={data.counts.checked_in} testid="door-count-checked-in" cls="bg-emerald-50 text-emerald-800" />
          <Count label="Not yet" value={data.counts.not_checked_in} testid="door-count-not-yet" cls="bg-amber-50 text-amber-800" />
        </div>
        {(!online || netDown) && <p data-testid="door-offline-banner" role="status" className="mt-3 flex items-center gap-2 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white"><WifiOff className="h-4 w-4" /> {OFFLINE}</p>}
      </header>
      {notice && <div className="px-4 pt-3"><DuplicateNotice notice={notice} label={label} onKeep={() => setNotice(null)} onUndo={undoAdd} /></div>}
      {visible.length === 0 ? (
        <div data-testid="door-empty" className="px-6 py-16 text-center">
          <p className="text-lg font-semibold text-slate-700">{data.guests.length === 0 ? "No guests on the list yet" : "No matching guest"}</p>
          <button type="button" data-testid="door-empty-add-btn" onClick={() => setAdding(true)} className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-5 text-base font-semibold text-blue-700"><UserPlus className="h-5 w-5" /> Add guest</button>
        </div>
      ) : (
        <ul data-testid="door-list" className="pb-24">
          {visible.map((g) => (
            <DoorRow key={g.id} guest={g} pending={pending[g.id]} error={errors[g.id]?.message}
              onCheckIn={() => setCheckIn(g, true)} onUndo={() => setCheckIn(g, false)}
              onRetry={() => setCheckIn(g, errors[g.id].checked)} onNames={() => setNameGuest(g)} />
          ))}
        </ul>
      )}
      <NameSheet guest={nameGuest} onClose={() => setNameGuest(null)} onSave={saveNames} />
      <AddGuestSheet open={adding} prefill={query.trim()} label={label} onClose={() => setAdding(false)} onAdd={addGuest} />
    </div>
  );
}
